import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import { requestAndApprovalApi } from "../services/request-and-approval.api";
import {
  getPlanningGroupVirtualClassroomId,
  type PlanningGroupEnrollmentFromApi,
  type PlanningGroupFromApi,
} from "../types/request-and-approval.types";
import VirtualClassroomFilesPanel from "./VirtualClassroomFilesPanel";
import styles from "./VirtualClassroomsListModal.module.scss";

function participantDisplayName(en: PlanningGroupEnrollmentFromApi): string {
  const full = [en.firstName, en.lastName].filter(Boolean).join(" ").trim();
  return full || en.userId || en.enrollmentId;
}

function participantInitials(en: PlanningGroupEnrollmentFromApi): string {
  const first = (en.firstName ?? "").trim()[0];
  const last = (en.lastName ?? "").trim()[0];
  const initials = `${first ?? ""}${last ?? ""}`.toUpperCase();
  if (initials) return initials;
  const name = en.userId || en.enrollmentId;
  return name.slice(0, 2).toUpperCase();
}

function avatarColor(seed: string): string {
  const palette = [
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#06b6d4",
    "#3b82f6",
    "#ef4444",
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return palette[Math.abs(hash) % palette.length];
}

function getDayLabel(day: number, t: (k: string) => string): string {
  const map: Record<number, string> = {
    1: "shortMonday",
    2: "shortTuesday",
    3: "shortWednesday",
    4: "shortThursday",
    5: "shortFriday",
    6: "shortSaturday",
    7: "shortSunday",
  };
  const key = map[day];
  return key ? t(`dayLabels.${key}`) : String(day);
}

export interface VirtualClassroomsListModalProps {
  isOpen: boolean;
  onClose: () => void;
  educationId: string;
  /** Üst bilgi satırı (eğitim adı vb.) */
  subtitle?: string;
}

function formatIsoDate(iso?: string | null): string {
  if (!iso?.trim()) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const VirtualClassroomsListModal: React.FC<VirtualClassroomsListModalProps> = ({
  isOpen,
  onClose,
  educationId,
  subtitle,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<PlanningGroupFromApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!educationId?.trim()) return;
    setLoading(true);
    setError(false);
    try {
      const { groups } = await requestAndApprovalApi.getPlanningGroups(
        educationId,
      );
      setItems(groups);
    } catch {
      setError(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [educationId]);

  useEffect(() => {
    if (isOpen && educationId) {
      void load();
    }
  }, [isOpen, educationId, load]);

  const title = t("virtualClassrooms.modal.title");

  const footer = (
    <Button variant="contained" color="primary" type="button" onClick={onClose}>
      {t("buttons.close")}
    </Button>
  );

  return (
    <Modal
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      customFooter={footer}
    >
      <div className={styles.body}>
        {subtitle ? <span className={styles.subtitle}>{subtitle}</span> : null}

        {loading ? (
          <div className={styles.skeletonList}>
            <div className={styles.skeletonCard} />
            <div className={styles.skeletonCard} />
          </div>
        ) : error ? (
          <div className={styles.errorBanner}>
            <span>⚠️</span>
            <span>
              {t("virtualClassrooms.modal.loadError")}
            </span>
          </div>
        ) : items.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>🎓</div>
            <h4 className={styles.emptyTitle}>
              {t("virtualClassrooms.modal.empty")}
            </h4>
            <p className={styles.emptyHint}>
              {t("virtualClassrooms.modal.emptyHint")}
            </p>
          </div>
        ) : (
          <ul className={styles.list}>
            {items.map((g, idx) => {
              const participantCount =
                g.enrollments?.length ?? g.enrollmentIds?.length ?? 0;
              const classroomId = getPlanningGroupVirtualClassroomId(g);
              const displayName = g.name?.trim() || `Sınıf ${idx + 1}`;
              return (
                <li
                  key={`${classroomId ?? "group"}-${idx}`}
                  className={styles.card}
                >
                  <div className={styles.cardHeader}>
                    <div className={styles.cardHeaderLeft}>
                      <div className={styles.classIcon}>
                        {displayName.charAt(0).toUpperCase()}
                      </div>
                      <div className={styles.classTitle}>
                        <strong>{displayName}</strong>
                        {classroomId ? (
                          <small>#{classroomId.slice(0, 8)}</small>
                        ) : null}
                      </div>
                    </div>
                    {g.jitsiRoomName?.trim() ? (
                      <span className={styles.jitsiChip} title={g.jitsiRoomName}>
                        📹 Jitsi
                      </span>
                    ) : null}
                  </div>

                  <div className={styles.metaGrid}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        📅{" "}
                        {t("virtualClassrooms.modal.start")}
                      </span>
                      <span className={styles.metaValue}>
                        {formatIsoDate(g.startDate)}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        🏁 {t("virtualClassrooms.modal.end")}
                      </span>
                      <span className={styles.metaValue}>
                        {formatIsoDate(g.endDate)}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        📍{" "}
                        {t("virtualClassrooms.modal.location")}
                      </span>
                      <span className={styles.metaValue}>
                        {g.location?.trim() || g.presetLocationId?.trim() || "—"}
                      </span>
                    </div>
                  </div>

                  {(g.scheduledTimes?.length ?? 0) > 0 ? (
                    <div className={styles.scheduleBlock}>
                      <div className={styles.scheduleHeader}>
                        🕐{" "}
                        {t("virtualClassrooms.modal.schedule")}
                      </div>
                      <div className={styles.scheduleChips}>
                        {(g.scheduledTimes ?? []).map((st, i) => (
                          <div key={i} className={styles.scheduleChip}>
                            <strong>
                              {getDayLabel(st.day, t)}
                            </strong>
                            <span>
                              {st.startTime} – {st.endTime}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className={styles.participantsBlock}>
                    <div className={styles.participantsHeader}>
                      <span className={styles.label}>
                        👥{" "}
                        {t("virtualClassrooms.modal.participants")}
                      </span>
                      <span className={styles.participantsCount}>
                        {participantCount}
                      </span>
                    </div>
                    <div className={styles.participantsList}>
                      {g.enrollments && g.enrollments.length > 0 ? (
                        g.enrollments.map((en) => {
                          const name = participantDisplayName(en);
                          const seed = en.userId || en.enrollmentId || name;
                          return (
                            <div
                              key={en.enrollmentId}
                              className={styles.participantChip}
                            >
                              <span
                                className={styles.avatar}
                                style={{ background: avatarColor(seed) }}
                              >
                                {participantInitials(en)}
                              </span>
                              <span className={styles.participantName}>
                                {name}
                              </span>
                              {en.title ? (
                                <span className={styles.participantTitle}>
                                  · {en.title}
                                </span>
                              ) : null}
                            </div>
                          );
                        })
                      ) : (g.enrollmentIds?.length ?? 0) > 0 ? (
                        (g.enrollmentIds ?? []).map((id) => (
                          <div key={id} className={styles.participantChip}>
                            <span
                              className={styles.avatar}
                              style={{ background: avatarColor(id) }}
                            >
                              {id.slice(0, 2).toUpperCase()}
                            </span>
                            <span className={styles.participantName}>
                              {id.slice(0, 8)}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className={styles.participantsEmpty}>
                          {t("virtualClassrooms.modal.noParticipants")}
                        </p>
                      )}
                    </div>
                  </div>

                  {classroomId ? (
                    <VirtualClassroomFilesPanel classroomId={classroomId} />
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
};

export default VirtualClassroomsListModal;
